/**
 * Room invitation codes — one source of truth for both the client (which lets
 * a host type or reroll a code) and the server (which registers and looks them
 * up). The alphabet drops the glyphs people misread aloud or mistype: I, O, 0
 * and 1.
 */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Codes are exactly this many characters. */
export const ROOM_CODE_LEN = 4;

/**
 * Canonicalize a hand-typed code: uppercased and trimmed. Returns null unless
 * the result is exactly `ROOM_CODE_LEN` characters, all from the alphabet —
 * it REJECTS a bad code rather than silently stripping characters, so
 * "AB0D" never quietly becomes a different room's code.
 */
export function normalizeRoomCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (code.length !== ROOM_CODE_LEN) return null;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return null;
  return code;
}

/** A fresh random code from the alphabet (collision-checked by the caller). */
export function randomRoomCode(): string {
  let code = '';
  for (let i = 0; i < ROOM_CODE_LEN; i++) {
    code += ROOM_CODE_ALPHABET[Math.floor(Math.random() * ROOM_CODE_ALPHABET.length)];
  }
  return code;
}
